import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import MenuIcon from '@mui/icons-material/Menu';
import { Avatar, IconButton, Tooltip } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import AppSelector from 'components/AppSelector/AppSelector';
import { TicketTabs } from 'components/Headers/TicketTabs';
import type { MenuOptionI } from 'components/Menu/BaseMenu';
import BaseMenu from 'components/Menu/BaseMenu';
import useReleaseNotePopup from 'components/ReleaseNotePopup/hooks/useReleaseNotePopup';
import ReleaseNotePopup from 'components/ReleaseNotePopup/ReleaseNotePopup';
import { useAuth } from 'contexts/AuthContext';
import { ThemeModeE, useThemeMode } from 'contexts/MaterialProvider';
import { useResponsive } from 'contexts/ResponsiveContext';
import { useUserProfile } from 'contexts/UserProfileContext';
import { StorageE } from 'declarations/enums/storage';
import { AVATAR_STYLES, getConsistentAvatarBackgroundColor } from 'helpers/avatar';
import { getInitials } from 'helpers/string';

export type NavTabItemT = {
  label: string;
  isActive: boolean;
  count?: number;
  /** When true, show (...) for loading state when count is undefined */
  hasCount?: boolean;
  onClick: () => void;
};

interface MainHeaderProps {
  /** Nav tabs (Tickets | Schedule | Insights) rendered inline in toolbar. WarrInsights-style. */
  navTabs?: NavTabItemT[];
  /** Opened ticket tabs for the current section - only shown when navTabs present and section is Tickets or Schedule */
  openedTicketTabs?: Record<string, string>;
  ticketTabIndex?: number;
  onTicketTabClick?: (ticketId: string, route: string) => void;
  onCloseTicketTab?: (e: React.MouseEvent, tabLabel: string, ticketId: string) => void;
}

const getTabCountSuffix = (tab: NavTabItemT, hideCount = false): string => {
  if (hideCount) return '';
  if (tab.count !== undefined) return ` (${tab.count})`;
  if (tab.hasCount) return ' (...)';
  return '';
};

const NavButton = styled(Button, {
  shouldForwardProp: prop => prop !== 'active',
})<{ active: boolean }>(({ active, theme }) => ({
  fontSize: '12px',
  fontWeight: 500,
  color: active ? 'rgba(0, 0, 0, 0.87)' : 'white',
  backgroundColor: active ? '#E0E0E0' : 'transparent',
  padding: '4px 8px',
  minWidth: 'auto',
  whiteSpace: 'nowrap',
  [theme.breakpoints.down('sm')]: {
    fontSize: '11px',
    padding: '2px 6px',
  },
  '&:hover': {
    backgroundColor: active ? 'white' : 'rgba(255, 255, 255, 0.08)',
    color: active ? 'rgba(0, 0, 0, 0.95)' : 'white',
  },
}));

/**
 * MainHeader component displays the top navigation bar for WarrChirp.
 * Nav tabs (Tickets | Schedule | Insights) are inline in the toolbar like WarrInsights.
 */
export const MainHeader: FC<MainHeaderProps> = ({
  navTabs = [],
  openedTicketTabs = {},
  ticketTabIndex = 0,
  onTicketTabClick,
  onCloseTicketTab,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { mode, toggleMode } = useThemeMode();
  const { signOut } = useAuth();
  const { userProfile, isSuccessfullyFetchedUserProfile } = useUserProfile();
  const { isMobile } = useResponsive();
  const isDarkMode = theme.palette.mode === 'dark';
  const { latestReleaseNote, isLoadingLatestReleaseNote } = useReleaseNotePopup();
  const [displayLatestReleaseNote, setDisplayLatestReleaseNote] = useState(false);

  const handleLogOut = useCallback(() => {
    signOut();
  }, [signOut]);

  const latestDismissedReleaseNoteId = localStorage.getItem(StorageE.LatestReleaseNoteId);
  const isDismissed = localStorage.getItem(StorageE.IsLatestReleaseNoteDismissed) === 'true';

  useEffect(() => {
    if (!isLoadingLatestReleaseNote && latestReleaseNote) {
      if (!latestDismissedReleaseNoteId || latestDismissedReleaseNoteId !== latestReleaseNote?.id) {
        localStorage.setItem(StorageE.LatestReleaseNoteId, latestReleaseNote?.id || '');
        setDisplayLatestReleaseNote(true);
      } else {
        if (!isDismissed) {
          setDisplayLatestReleaseNote(true);
        }
      }
    }
  }, [isLoadingLatestReleaseNote, latestReleaseNote, latestDismissedReleaseNoteId, isDismissed]);

  const menuItems: MenuOptionI[] = useMemo<MenuOptionI[]>(
    () => [{ optionText: 'Sign Out', callback: handleLogOut }],
    [handleLogOut]
  );

  const hasOneOfNames = userProfile?.firstname || userProfile?.lastname;

  const userName = hasOneOfNames
    ? `${userProfile?.firstname ?? ''} ${userProfile?.lastname ?? ''}`.trim()
    : userProfile?.displayName ?? '';

  const hasTicketTabs =
    navTabs.length > 0 &&
    Object.keys(openedTicketTabs).length > 0 &&
    onTicketTabClick &&
    onCloseTicketTab;

  return (
    <Box>
      <AppBar
        sx={{
          top: 0,
          position: 'fixed',
          boxShadow: 'none',
          borderBottom: (t: { palette: { divider: string } }) => `1px solid ${t.palette.divider}`,
          backgroundColor: theme =>
            theme.palette.background?.paperElevation1 ||
            theme.palette.background?.default ||
            theme.palette.background?.paper ||
            '#FAFAFA',
        }}
      >
        <Box sx={{ bgcolor: theme => theme.palette.header?.blue || '#182955', px: 4 }}>
          <Toolbar disableGutters>
            <Box sx={{ width: isMobile ? 'auto' : '50%', display: 'flex', alignItems: 'center' }}>
              {!isMobile && (
                <>
                  <Typography
                    variant="h6"
                    noWrap
                    component="a"
                    onClick={(): void => navigate('/')}
                    sx={{
                      mr: 2,
                      fontWeight: 700,
                      color: 'inherit',
                      textDecoration: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    WarrChirp
                  </Typography>
                </>
              )}
              {navTabs.length > 0 && (
                <Box
                  sx={{
                    ml: isMobile ? 0 : 3,
                    display: 'flex',
                    gap: isMobile ? 0.5 : 2,
                    flexWrap: 'nowrap',
                    alignItems: 'center',
                  }}
                >
                  {navTabs.map(tab => (
                    <NavButton key={tab.label} active={tab.isActive} onClick={tab.onClick}>
                      {tab.label}
                      {getTabCountSuffix(tab, isMobile)}
                    </NavButton>
                  ))}
                </Box>
              )}
            </Box>
            <Box sx={{ width: isMobile ? 'auto' : '50%' }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                {!isMobile && isSuccessfullyFetchedUserProfile && userName && (
                  <Typography variant="h5" sx={{ mr: 1 }}>
                    Welcome, {userName}.
                  </Typography>
                )}

                {!isMobile && (
                  <Tooltip
                    title={isSuccessfullyFetchedUserProfile ? `${userName} (Me)` : ''}
                    arrow
                    placement="bottom"
                  >
                    <Avatar
                      sx={{
                        backgroundColor: getConsistentAvatarBackgroundColor(
                          userProfile?.userId,
                          isDarkMode
                        ),
                        ...AVATAR_STYLES.size,
                        fontSize: AVATAR_STYLES.fontSize,
                      }}
                    >
                      {isSuccessfullyFetchedUserProfile ? getInitials(userName || '') : ''}
                    </Avatar>
                  </Tooltip>
                )}
                {!isMobile && <Box id="pendo-badge-anchor" sx={{ width: 30, height: 30 }} />}
                <IconButton
                  aria-label="Toggle theme"
                  onClick={toggleMode}
                  sx={{ color: t => t.palette.common.white, ml: 1, mr: -1.75 }}
                >
                  {mode === ThemeModeE.Dark ? (
                    <LightModeIcon sx={{ width: 24, height: 24 }} />
                  ) : (
                    <DarkModeIcon sx={{ width: 24, height: 24 }} />
                  )}
                </IconButton>
                <AppSelector />
                <BaseMenu
                  icon={<MenuIcon sx={{ width: 30, height: 30, color: 'common.white' }} />}
                  menuItems={menuItems}
                  boxProps={{ sx: { ml: -0.5 } }}
                />
              </Box>
            </Box>
          </Toolbar>
        </Box>
        {hasTicketTabs && onTicketTabClick && onCloseTicketTab && (
          <TicketTabs
            tabs={openedTicketTabs}
            tabIndex={ticketTabIndex}
            onTabClick={onTicketTabClick}
            onCloseTab={onCloseTicketTab}
          />
        )}
      </AppBar>
      {displayLatestReleaseNote && latestReleaseNote && (
        <ReleaseNotePopup
          displayNotes={displayLatestReleaseNote}
          latestReleaseNote={latestReleaseNote}
        />
      )}
    </Box>
  );
};
